import _ from 'lodash';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    ORDERED_BUDGET_PHASES,
    BudgetBys,
    BudgetPhases,
    DEFAULT_BUDGET_BY,
} from 'business/constants';
import { ALL_BUDGETS_VALUE } from 'routes/BudgetModule/components/BudgetOverview/constants';
import { BudgetSelectionT } from 'routes/BudgetModule/components/shared/BudgetLibrary/types';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { QueryClient } from '@tanstack/react-query';
import { arrayMove } from '@dnd-kit/sortable';

/** Filter and sort budget proposals with exercice and a list of budget names. */
export function filterBudgetProposals(
    budgetProposals: BudgetProposalT[],
    budgetName: string | null,
    exercice: string,
): BudgetProposalT[] {
    const filteredByExercice = budgetProposals.filter((bp) =>
        exercice !== '' ? exercice === bp.exercice.toString() : true,
    );
    if (budgetName === ALL_BUDGETS_VALUE) {
        // All budgets are selected. There is no filtering by budget name.
        return filteredByExercice;
    }

    return budgetProposals
        .filter((bp) =>
            exercice !== '' ? exercice === bp.exercice.toString() : true,
        )
        .filter((bp) => (budgetName ? budgetName === bp.budgetName : true))
        .sort(
            (a: BudgetProposalT, b: BudgetProposalT) =>
                ORDERED_BUDGET_PHASES.indexOf(a.phase) -
                ORDERED_BUDGET_PHASES.indexOf(b.phase),
        );
}

/** Filter budget proposals with exercice. */
export function filterBudgetProposalsByExercice(
    budgetProposals: BudgetProposalT[],
    exercice: string,
): BudgetProposalT[] {
    return budgetProposals
        .filter((bp) =>
            exercice !== '' ? exercice === bp.exercice.toString() : true,
        )
        .sort(
            (a: BudgetProposalT, b: BudgetProposalT) =>
                ORDERED_BUDGET_PHASES.indexOf(a.phase) -
                ORDERED_BUDGET_PHASES.indexOf(b.phase),
        );
}

/**
 * Sorts budget proposals based on their display position, archival status, and ID.
 *
 * Sorting criteria:
 * 1. BP with a valid `displayPosition` come first (lower values first).
 * 2. BP without a valid `displayPosition` are placed at the end.
 * 3. Archived proposals (`BudgetPhases.ARCHIVED`) are moved to the end.
 * 4. If all above criteria are equal, proposals are sorted by their ID.
 */
export function sortBudgetProposals(
    bp1: BudgetProposalT,
    bp2: BudgetProposalT,
): number {
    const displayPosition1 = bp1.displayPosition ?? -1;
    const displayPosition2 = bp2.displayPosition ?? -1;
    if (displayPosition1 === -1 && displayPosition2 !== -1) return 1;
    if (displayPosition1 !== -1 && displayPosition2 === -1) return -1;
    if (displayPosition1 !== displayPosition2)
        return displayPosition1 - displayPosition2;

    if (
        bp1.phase === BudgetPhases.ARCHIVED &&
        bp2.phase !== BudgetPhases.ARCHIVED
    )
        return 1;
    if (
        bp2.phase === BudgetPhases.ARCHIVED &&
        bp1.phase !== BudgetPhases.ARCHIVED
    )
        return -1;

    return bp1.id - bp2.id;
}

/** Get exercices from a list of budget proposals. */
export function getExercicesOfBudget(
    budgetProposals: BudgetProposalT[],
): number[] {
    return _(budgetProposals)
        .map((bp) => bp.exercice)
        .uniq()
        .value();
}

const getUniqueBudgetNames = (budgetProposals: BudgetProposalT[]) =>
    _(budgetProposals)
        .map((bp) => bp.budgetName)
        .uniq()
        .value();

const getBudgetOptionsForExercice = (
    budgetNamesWithArchivedStatus: { name: string; isArchived: boolean }[],
    budgetProposals: BudgetProposalT[],
    exercice: string,
): BudgetSelectionT[] =>
    _.compact(
        budgetNamesWithArchivedStatus.map((bpNameWithArchivedStatus) => {
            const { isArchived, name: bpName } = bpNameWithArchivedStatus;
            const budgetProposal = budgetProposals.find(
                (budget) =>
                    budget.budgetName === bpName &&
                    exercice === budget.exercice.toString(),
            );
            if (!budgetProposal) return null;
            return {
                value: budgetProposal.budgetSoftwareId ?? '',
                label: bpName,
                isBadValue: false,
                isArchived,
                id: budgetProposal.id,
            };
        }),
    );

/**
 * For a given budget name, check if budget proposals from both section are archived
 */
const getArchivedStatusForBudget = (name: string, bps: BudgetProposalT[]) => {
    const budgetProposalsMatchingName = bps.filter(
        (budget) => budget.budgetName === name,
    );
    const isArchived = budgetProposalsMatchingName.every(
        (budget) => budget.phase === BudgetPhases.ARCHIVED,
    );
    return {
        name,
        isArchived,
    };
};

/** Dict of exercices and the different BP existing in this year */
export function getSortedBudgetNamesWithArchivedStatusByYear(
    budgetProposals: BudgetProposalT[],
): {
    [key: number]: BudgetSelectionT[];
} {
    const bpsByYear = _.groupBy(budgetProposals, (bp) => bp.exercice);
    return _.reduce(
        bpsByYear,
        (acc: Record<string, BudgetSelectionT[]>, bps, year) => {
            const sortedBps = bps.sort(sortBudgetProposals);
            const uniqueBudgetNames = getUniqueBudgetNames(sortedBps);
            const budgetNamesWithArchivedStatus = uniqueBudgetNames.map(
                (budgetName) =>
                    getArchivedStatusForBudget(budgetName, sortedBps),
            );
            const values = getBudgetOptionsForExercice(
                budgetNamesWithArchivedStatus,
                budgetProposals,
                year,
            );
            acc[year] = values;
            return acc;
        },
        {},
    );
}

/** Dict of BudgetName and the different exercice available for this budget */
export function getExercicesByBudgetName(budgetProposals: BudgetProposalT[]): {
    [key: string]: number[];
} {
    const exerciceByBPs = _.groupBy(budgetProposals, (bp) => bp.budgetName);
    const exercicesByBudgetName: { [key: string]: number[] } = {};
    for (const [name, bps] of Object.entries(exerciceByBPs)) {
        const bpExercices = _(bps)
            .map((bp) => bp.exercice)
            .uniq()
            .value();
        exercicesByBudgetName[name] = bpExercices;
    }

    const allExistingExercices = _(exercicesByBudgetName)
        .values()
        .flatten()
        .uniq()
        .value();

    exercicesByBudgetName[ALL_BUDGETS_VALUE] = allExistingExercices;
    return exercicesByBudgetName;
}

/** From a list of budget proposal and a current year, return the first budget by found
 * in the current year. If none were found, return the first in all of the list.
 * This is assuming all bp's have the same ventilation within the same year, which is not true */
export function getBudgetByFromListOfBudgetProposal(
    budgetProposals: BudgetProposalT[],
    currentExercice: number,
): BudgetBys {
    // Fix this !
    const budgetProposalsOfCurrentYear = budgetProposals.filter(
        (bp) => bp.exercice === currentExercice,
    );
    if (budgetProposalsOfCurrentYear.length > 0) {
        return budgetProposalsOfCurrentYear[0].by;
    }
    return budgetProposals.length > 0
        ? budgetProposals[0].by
        : DEFAULT_BUDGET_BY;
}

/**
 * Sort budget select options by status (archived are last) and then by id.
 * @returns a list of budget name
 */
export const getSortedBudgetNameOptions = (
    budgetOptionsforSelectedExercice: BudgetSelectionT[] | undefined | null,
): BudgetSelectionT[] => {
    const [archivedBudgetNames, budgetNames] = _.partition(
        budgetOptionsforSelectedExercice,
        (budget) => budget.isArchived,
    );
    const sortedArchivedBudgetNames = _.orderBy(
        archivedBudgetNames,
        ['id'],
        ['asc'],
    );
    const sortedBudgetNames = _.orderBy(budgetNames, ['id'], ['asc']);
    const sortedBudgetNamesByYear = sortedBudgetNames.concat(
        sortedArchivedBudgetNames,
    );
    return sortedBudgetNamesByYear;
};

/**
 * Find the budget proposal by its id in the list of budget proposals.
 */
export const findBudgetProposalById = (
    id: number,
    budgetProposals: BudgetProposalT[],
): BudgetProposalT | undefined => {
    return budgetProposals.find((bp) => bp.id === id);
};

/**
 * This function ensures that when a budget proposal is dragged and dropped, the position
 * of all budget proposal with the same budget are updated within each section.
 */
export const updateBudgetProposalDisplayInSection = (
    sectionBpIdsMap: Record<string, number[]>,
    movedBp: BudgetProposalT,
    destinationBp: BudgetProposalT,
    budgetProposals: BudgetProposalT[],
) => {
    const updatedSectionBpIdsMap = {
        ...sectionBpIdsMap,
    };

    Object.entries(updatedSectionBpIdsMap).forEach(([section, bpIds]) => {
        const movedBpInSection = budgetProposals.find(
            (bp) =>
                bp.budgetSoftwareId === movedBp.budgetSoftwareId &&
                (bp.section ?? 'null') === section,
        );
        const destinationBpInSection = budgetProposals.find(
            (bp) =>
                bp.budgetSoftwareId === destinationBp.budgetSoftwareId &&
                (bp.section ?? 'null') === section,
        );

        if (movedBpInSection && destinationBpInSection) {
            updatedSectionBpIdsMap[section] = arrayMove(
                bpIds,
                bpIds.indexOf(movedBpInSection.id),
                bpIds.indexOf(destinationBpInSection.id),
            );
        }
    });
    return updatedSectionBpIdsMap;
};

/**
 * Update the display position of the budget proposal in the query data.
 */
export const updateBudgetProposalDisplayPositionQueryData = (
    sectionBpIdsMap: Record<string, number[]>,
    queryClient: QueryClient,
) => {
    const { budgetProposalsKeys } = createBudgetQueryKeys();
    queryClient.setQueryData(
        budgetProposalsKeys(),
        (oldBudgetProposals: BudgetProposalT[]) =>
            oldBudgetProposals.map((bp) => {
                const newIndex = sectionBpIdsMap[bp.section ?? 'null'].indexOf(
                    bp.id,
                );
                if (newIndex === -1) {
                    return bp;
                }
                return {
                    ...bp,
                    displayPosition: newIndex,
                };
            }),
    );
};
